import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, Divider } from 'antd'
import { RightOutlined, DownOutlined } from '@ant-design/icons'
import { Share2, FileText } from 'lucide-react'
import { useMindMapContext } from '../../../../context/mindmap'
import { useLabelContext } from '../../../../context/label'
import {
  useLabelFilterContext,
  useVisibleNodeIds,
  buildParentMap,
  buildNodeLabelMap,
  LabelFilterUI,
} from '../../../../features/labelFilter'
import { SidebarHeader } from '../../../common/SidebarHeader'
import { useFeatureAccessContext } from '../../../../context/featureAccess'
import { UpgradePromptModal } from '../../../UpgradePromptModal/UpgradePromptModal'
import type { Node } from '@xyflow/react'
import type {
  ReactFlowNodeData,
  SummarizedNodeData,
  StoryboardNodeData,
} from '../../../../types/mindmap-components'
import {
  getRootStandaloneIdeas,
  getRootNeeds,
  getChildNeeds,
  filterNodesByType,
} from '../../../../utils/reportHierarchyUtils'
import {
  VentureSection,
  NeedSection,
  StandaloneIdeasSection,
  StoryboardsSection,
} from '../../../Report/ReportComponents'
import type { StoryboardData } from '../../../Report/ReportComponents'
import {
  StyledSider,
  SidebarContent,
  StickyFilterBar,
  SidebarBody,
  StickyExportBar,
  ExportButton,
  ExportButtonIcon,
  ExportButtonText,
  EmptyState,
} from './styles/styledComponents'
import { EmptyStateText } from './styles/ReportSidebarStyles'
import { useReportSidebar } from './useReportSidebar'
import { ExportSelectionBanner } from './ExportSelectionBanner'

const { Text } = Typography

interface ReportSidebarProps {
  isVisible: boolean
  onClose: () => void
  onNodeClick?: (nodeId: string) => void
}

export function ReportSidebar({ isVisible, onClose, onNodeClick }: ReportSidebarProps) {
  const navigate = useNavigate()
  const { nodes, edges, mindmapId } = useMindMapContext()
  const { labels: projectLabels, getNodeLabels, nodeLabelsVersion } = useLabelContext()

  // Use shared filter context for unified state across views
  const { selectedFilterLabels, setSelectedFilterLabels } = useLabelFilterContext()

  // Feature access for tier-based checks
  const { checkFeature, isFeatureEnabled } = useFeatureAccessContext()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('')

  // Helper to get effective node type (handles summarized/collapsed nodes)
  const getEffectiveNodeType = useCallback((node: Node<ReactFlowNodeData>): string => {
    if (node.type === 'summarizedNode') {
      const data = node.data as SummarizedNodeData
      return data.originalNodeType || node.type
    }
    return node.type ?? ''
  }, [])

  // Helper to get effective node data (handles summarized/collapsed nodes)
  const getEffectiveNodeData = useCallback((node: Node<ReactFlowNodeData>): ReactFlowNodeData => {
    if (node.type === 'summarizedNode') {
      const data = node.data as SummarizedNodeData
      return data.originalNodeData
    }
    return node.data
  }, [])

  // Build maps for filtering using shared utilities
  const nodeLabelMap = useMemo(
    () =>
      buildNodeLabelMap(
        nodes.map((n) => n.id),
        getNodeLabels
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nodeLabelsVersion is used as invalidation trigger
    [nodes, getNodeLabels, nodeLabelsVersion]
  )

  const parentMap = useMemo(() => buildParentMap(edges), [edges])

  // Branch selection filter for export
  const { selectedNodeIds: exportSelectedNodeIds } = useReportSidebar()

  // Use shared visibility logic
  const { shouldDisplayNode: shouldDisplayNodeByLabel } = useVisibleNodeIds({
    nodeLabelMap,
    parentMap,
    allNodeIds: nodes.map((n) => n.id),
    selectedFilterLabels,
  })

  // Combine label filter with branch selection filter
  const shouldDisplayNode = useCallback(
    (nodeId: string): boolean => {
      if (!shouldDisplayNodeByLabel(nodeId)) return false
      if (exportSelectedNodeIds && !exportSelectedNodeIds.has(nodeId)) return false
      return true
    },
    [shouldDisplayNodeByLabel, exportSelectedNodeIds]
  )

  // Get child nodes of a parent
  const getChildNodes = useCallback(
    (parentId: string): Node<ReactFlowNodeData>[] => {
      const childEdges = edges.filter((edge) => edge.source === parentId)
      const childNodes = childEdges
        .map((edge) => nodes.find((node) => node.id === edge.target))
        .filter((node): node is Node<ReactFlowNodeData> => node !== undefined)
        .filter((node) => shouldDisplayNode(node.id))

      // Deduplicate by node id
      return Array.from(new Map(childNodes.map((node) => [node.id, node])).values())
    },
    [edges, nodes, shouldDisplayNode]
  )

  // Get child IDEAS specifically (filtered by type, sorted by visual weight asc — solo nodes first)
  const getChildIdeas = useCallback(
    (parentId: string): Node<ReactFlowNodeData>[] => {
      const ideas = getChildNodes(parentId).filter(
        (node) =>
          node.type === 'ideaNode' ||
          (node.type === 'summarizedNode' && getEffectiveNodeType(node) === 'ideaNode')
      )
      // Recursive visual weight: each idea child = 1, storyboard/survey = 10 (takes much more space)
      const nodeScore = (id: string, visited = new Set<string>()): number => {
        if (visited.has(id)) return 0
        const next = new Set(visited).add(id)
        return edges
          .filter((e) => e.source === id)
          .reduce((sum, e) => {
            const n = nodes.find((node) => node.id === e.target)
            if (!n) return sum
            const effectiveType = n.type === 'summarizedNode' ? getEffectiveNodeType(n) : n.type
            if (effectiveType === 'ideaNode') return sum + 1 + nodeScore(n.id, next)
            if (effectiveType === 'storyboardNode') return sum + 10
            return sum
          }, 0)
      }
      return ideas.sort((a, b) => nodeScore(a.id) - nodeScore(b.id))
    },
    [getChildNodes, getEffectiveNodeType, edges, nodes]
  )

  // Build hierarchy config for utility functions
  const hierarchyConfig = useMemo(() => {
    return {
      nodes,
      edges,
      getEffectiveNodeType,
      shouldDisplayNode,
    }
  }, [nodes, edges, getEffectiveNodeType, shouldDisplayNode])

  // Find ALL company nodes (including collapsed/summarized ones)
  const ventureNodes = useMemo(() => {
    return filterNodesByType(nodes, 'ventureNode', hierarchyConfig)
  }, [nodes, hierarchyConfig])

  // Find standalone unmet needs (not under any venture)
  const standaloneNeeds = useMemo(() => {
    return getRootNeeds(hierarchyConfig)
  }, [hierarchyConfig])

  // Find root standalone ideas, sorted by visual weight asc (solo nodes first)
  const standaloneIdeas = useMemo(() => {
    const ideas = getRootStandaloneIdeas(hierarchyConfig)
    // Recursive visual weight: each idea child = 1, storyboard = 10
    const nodeScore = (id: string, visited = new Set<string>()): number => {
      if (visited.has(id)) return 0
      const next = new Set(visited).add(id)
      return edges
        .filter((e) => e.source === id)
        .reduce((sum, e) => {
          const n = nodes.find((node) => node.id === e.target)
          if (!n) return sum
          const effectiveType = n.type === 'summarizedNode' ? getEffectiveNodeType(n) : n.type
          if (effectiveType === 'ideaNode') return sum + 1 + nodeScore(n.id, next)
          if (effectiveType === 'storyboardNode') return sum + 10
          return sum
        }, 0)
    }
    return ideas.sort((a, b) => nodeScore(a.id) - nodeScore(b.id))
  }, [hierarchyConfig, edges, nodes, getEffectiveNodeType])

  // Helper to get child unmet needs for a venture
  const getVentureNeeds = useCallback(
    (ventureId: string) => {
      return getChildNeeds(ventureId, hierarchyConfig)
    },
    [hierarchyConfig]
  )

  const handleExportPDF = useCallback(() => {
    const { hasAccess } = checkFeature('pdf_export')
    if (!hasAccess) {
      setUpgradeFeatureName('PDF export')
      setShowUpgradeModal(true)
      return
    }

    if (mindmapId) {
      if (exportSelectedNodeIds && exportSelectedNodeIds.size > 0) {
        const ids = [...exportSelectedNodeIds]
        sessionStorage.setItem(`report-selection-${mindmapId}`, JSON.stringify(ids))
      } else {
        sessionStorage.removeItem(`report-selection-${mindmapId}`)
      }
      void navigate(`/mindmap/${mindmapId}/report`)
    }
  }, [mindmapId, checkFeature, navigate, exportSelectedNodeIds])

  // Adapter for node click - shared components use (e, nodeId), but our prop uses (nodeId)
  const handleNodeClick = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      e.stopPropagation()
      onNodeClick?.(nodeId)
    },
    [onNodeClick]
  )

  // Helper to get labels for a node (for shared components)
  const getLabelsForNode = useCallback(
    (nodeId: string) => {
      const labelIds = nodeLabelMap.get(nodeId) ?? []
      return labelIds
        .map((id) => projectLabels.find((l) => l.id === id))
        .filter((l): l is NonNullable<typeof l> => l !== undefined)
    },
    [nodeLabelMap, projectLabels]
  )

  // Get storyboard children for an idea node
  const getChildStoryboards = useCallback(
    (ideaId: string): StoryboardData[] => {
      return edges
        .filter((edge) => edge.source === ideaId)
        .map((edge) => nodes.find((node) => node.id === edge.target))
        .filter(
          (node): node is Node<ReactFlowNodeData> =>
            node !== undefined && node.type === 'storyboardNode'
        )
        .map((node) => {
          const data = node.data as StoryboardNodeData
          return {
            id: node.id,
            storyboardTitle: data.storyboardTitle,
            storyboardUrls: data.storyboardUrls,
            frameCaptions: data.frameCaptions,
            isGenerating: data.isGenerating,
          }
        })
    },
    [edges, nodes]
  )

  const renderIdeaStoryboards = useCallback(
    (nodeId: string) => {
      const storyboards = getChildStoryboards(nodeId)
      if (storyboards.length === 0) return null
      return (
        <StoryboardsSection
          storyboards={storyboards}
          onNodeClick={onNodeClick ? handleNodeClick : undefined}
        />
      )
    },
    [getChildStoryboards, onNodeClick, handleNodeClick]
  )

  if (nodes.length === 0) {
    return (
      <StyledSider width={800} collapsed={!isVisible} collapsedWidth={0} trigger={null}>
        <SidebarContent>
          <SidebarHeader
            collapsed={false}
            icon={<Share2 size={16} />}
            title="Export"
            desktopIcon={<RightOutlined />}
            mobileIcon={<DownOutlined />}
            onToggle={onClose}
          />
          <SidebarBody>
            <EmptyState>
              <Share2 size={48} color="#d9d9d9" />
              <Text type="secondary">
                <EmptyStateText>No content to display</EmptyStateText>
              </Text>
            </EmptyState>
          </SidebarBody>
        </SidebarContent>
      </StyledSider>
    )
  }

  return (
    <>
      <StyledSider width={600} collapsed={!isVisible} collapsedWidth={0} trigger={null}>
        <SidebarContent>
          <SidebarHeader
            collapsed={false}
            icon={<Share2 size={16} />}
            title="Export"
            desktopIcon={<RightOutlined />}
            mobileIcon={<DownOutlined />}
            onToggle={onClose}
          />

          {/* Selection banner - shows which branches are selected for export */}
          <ExportSelectionBanner />

          {/* Sticky Filter Bar - uses unified LabelFilterUI component */}
          {isFeatureEnabled('labels') && projectLabels.length > 0 && (
            <StickyFilterBar>
              <LabelFilterUI
                variant="inline"
                selectedLabels={selectedFilterLabels}
                onLabelsChange={setSelectedFilterLabels}
                availableLabels={projectLabels}
              />
            </StickyFilterBar>
          )}

          <SidebarBody>
            {/* ============================================================
                HIERARCHICAL REPORT STRUCTURE (Nested Cards)
                Venture → Unmet needs → Ideas
                ============================================================ */}

            {/* 1. VENTURES with nested unmet needs and ideas */}
            {ventureNodes.map((ventureNode) => {
              const ventureData = getEffectiveNodeData(ventureNode)
              const childNeeds = getVentureNeeds(ventureNode.id)

              return (
                <VentureSection
                  key={ventureNode.id}
                  nodeId={ventureNode.id}
                  data={{
                    companyName: ventureData.companyName,
                    businessModelAndValueProp: ventureData.businessModelAndValueProp,
                    capabilitiesAndAssets: ventureData.capabilitiesAndAssets,
                    hardConstraints: ventureData.hardConstraints,
                  }}
                  onNodeClick={onNodeClick ? handleNodeClick : undefined}
                >
                  {/* Nested unmet needs inside venture card */}
                  {childNeeds.map((need) => {
                    const needData = getEffectiveNodeData(need)
                    return (
                      <NeedSection
                        key={need.id}
                        nodeId={need.id}
                        data={{ label: needData.label }}
                        getChildIdeas={getChildIdeas}
                        getIdeaData={(node) => ({ label: getEffectiveNodeData(node).label })}
                        getNodeId={(node) => node.id}
                        getNodeLabels={getLabelsForNode}
                        onNodeClick={onNodeClick ? handleNodeClick : undefined}
                        renderIdeaExtra={renderIdeaStoryboards}
                      />
                    )
                  })}
                </VentureSection>
              )
            })}

            {/* 2. STANDALONE UNMET NEEDS (not under any venture) */}
            {standaloneNeeds.length > 0 && (
              <>
                {ventureNodes.length > 0 && <Divider />}
                {standaloneNeeds.map((need) => {
                  const needData = getEffectiveNodeData(need)
                  return (
                    <NeedSection
                      key={need.id}
                      nodeId={need.id}
                      data={{ label: needData.label }}
                      getChildIdeas={getChildIdeas}
                      getIdeaData={(node) => ({ label: getEffectiveNodeData(node).label })}
                      getNodeId={(node) => node.id}
                      getNodeLabels={getLabelsForNode}
                      onNodeClick={onNodeClick ? handleNodeClick : undefined}
                      renderIdeaExtra={renderIdeaStoryboards}
                    />
                  )
                })}
              </>
            )}

            {/* 3. STANDALONE IDEAS (not under any unmet need) */}
            {standaloneIdeas.length > 0 && (
              <>
                {(ventureNodes.length > 0 || standaloneNeeds.length > 0) && <Divider />}
                <StandaloneIdeasSection
                  ideas={standaloneIdeas}
                  getChildIdeas={getChildIdeas}
                  getIdeaData={(node) => ({ label: getEffectiveNodeData(node).label })}
                  getNodeId={(node) => node.id}
                  getNodeLabels={getLabelsForNode}
                  onNodeClick={onNodeClick ? handleNodeClick : undefined}
                  renderIdeaExtra={renderIdeaStoryboards}
                />
              </>
            )}

            {/* Empty state when nothing to show */}
            {ventureNodes.length === 0 &&
              standaloneNeeds.length === 0 &&
              standaloneIdeas.length === 0 && (
                <EmptyState>
                  <Share2 size={48} color="#d9d9d9" />
                  <Text type="secondary">
                    <EmptyStateText>Add content to see the report</EmptyStateText>
                  </Text>
                </EmptyState>
              )}
          </SidebarBody>

          {/* Sticky Export Button */}
          <StickyExportBar>
            <ExportButton onClick={handleExportPDF}>
              <ExportButtonIcon>
                <FileText size={20} />
              </ExportButtonIcon>
              <ExportButtonText>Export as PDF</ExportButtonText>
            </ExportButton>
          </StickyExportBar>
        </SidebarContent>
      </StyledSider>

      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName={upgradeFeatureName}
      />
    </>
  )
}
